import {
  Button,
  Checkbox,
  Collapse,
  Divider,
  Grid,
  Input,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import moment from 'moment';
import React from 'react';

import { ABTestMode } from '~/src/dash/api/ab-test';
import {
  HeaderBidderPoddedRequestActiveActive,
  IABTesting,
  IPublisher,
} from '~/src/dash/api/console-api';
import { convertECurrencyTypeToSymbol } from '~/src/dash/api/exchange-rates';
import { EBool } from '~/src/dash/api/types/bool';
import ConfirmationDialogButton from '~/src/dash/components/confirmation-dialog-button';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { ModernTextField } from '~/src/dash/components/modern-text-field';
import {
  BIDDERS_MULTI_BID_ENABLED,
  BIDDERS_PODDED_REQUEST_ENABLED,
} from '~/src/dash/globals/constants';
import { Col, Cols, Row, Rows } from '~/src/dash/publicaUI';
import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import { formatCurrency } from '~/src/dash/utils/format';
import { smallRadius } from '~/src/dash/utils/material-dashboard-react';
import { IErrors } from '~/src/dash/utils/validator';
import { generateSlotOptions } from '~/src/dash/views/bidder-settings-form-view';
import { DemandSource, DemandSources } from '~/src/dash/globals/demand-sources';

interface IProps {
  // testType indicates if this is a new, current, or past test
  testType: ABTestMode;
  // index is the unique ID of the test
  index: number;
  // bidder params shows current A test values
  bidderParams: any;
  // errors are test-panel specific
  errors?: IErrors;
  // disabled reflects user write rights
  disabled: boolean;
  // new test keeps track of new test inputs
  newTest?: any;
  // abTest is the full existing test object
  abTest?: IABTesting;
  // ATest share renders share of traffic to existing params
  ATestShare?: string;
  // abTestCollapsibleStatus maps test IDs to booleans
  abTestCollapsibleStatus: INumberBoolMap;
  // handleCollapse updates a specific panel's collapsible status
  handleCollapse: (index: number) => void;
  // handleChange handles input changes
  handleChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;

  implementParameters?: (index: number) => void;
  endTest?: (index: number) => void;
  launchTest?: () => void;
  handleCancel?: () => void;
  publisher: IPublisher;
  renderFlightDates: (abTest?: IABTesting) => void;
}

interface INumberBoolMap {
  [key: number]: boolean;
}

const parseJSONParamsSafely = (str: string) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    // Return an empty object
    // Future refactor: create interface for params and parse once in console-api
    return {};
  }
};

const parseBidderParamsSafely = (str: string) => {
  try {
    return JSON.parse('{' + str + '}');
  } catch (e) {
    // Return an empty object
    return {};
  }
};

const getNumberOfAds = (bidderParams: string): string => {
  const params = parseBidderParamsSafely(bidderParams);
  const numberOfAds = params.number_of_ads;
  if (numberOfAds) {
    return '' + numberOfAds;
  }
  return 'None';
};

const getSpotxNumberOfAds = (bidderParams: string): string => {
  const params = parseBidderParamsSafely(bidderParams);
  const numberOfAds = params.additional_bids;
  if (numberOfAds) {
    return '' + numberOfAds;
  }
  return 'None';
};

// generates the nubmer_of_ads options for the dropdown
const generateNumberOfAdsOptions = () => {
  const numberOfAds = [] as Array<{ key: string; text: string }>;
  for (let i = 1; i <= 8; i += 1) {
    numberOfAds.push({ key: '' + i, text: '' + i });
  }
  return numberOfAds;
};

export const ABTestPanel = (props: IProps) => {
  const { testType, index, abTest, abTestCollapsibleStatus, publisher } = props;

  const confirmLaunchTestDescription =
    'Test parameters will be set and requests will start splitting between Bidder Parameters A and Bidder Test Parameters B.';
  const confirmLaunchTestNotice =
    'Launching will prevent changes on the source bidder’s test parameters.';
  const confirmImplementTestParamsDescription =
    'Bidder Test Parameters B will override the current Bidder A parameters in production.';
  const confirmEndTestDescription = 'Are you sure you want to end this test?';

  // Test running before this change appear whit abTest.EndDate as null, so the last check helps to prevent those test to stop or fail
  if (
    testType === ABTestMode.Current &&
    abTest !== undefined &&
    props.endTest &&
    abTest.EndDate !== undefined &&
    'null'
  ) {
    const eDate = new Date(abTest.EndDate);
    const currentDate = moment().toDate().getTime();
    if (abTest.Active === 2 && eDate.getTime() <= currentDate) {
      props.endTest(index);
    }
  }

  const handleLaunchTest = () => {
    if (props.launchTest) {
      props.launchTest();
    }
  };

  const handleImplementParameters = (index: number) => {
    if (props.implementParameters) {
      props.implementParameters(index);
    }
  };

  const handleEndTest = (index: number) => {
    if (props.endTest) {
      props.endTest(index);
    }
  };

  const handleCancelTest = () => {
    if (props.handleCancel) {
      props.handleCancel();
    }
  };

  const renderExistingTest = () => {
    const changedFields: string[] = [];
    if (props.abTest) {
      const testBParams = parseJSONParamsSafely(props.abTest.Tests[1].Parameters);
      if (Object.keys(testBParams).length > 0) {
        for (const key in testBParams) {
          if (testBParams.hasOwnProperty(key)) {
            changedFields.push(key);
          }
        }
      }
    }

    return (
      <Grid direction='column'>
        {props.renderFlightDates(props.abTest)}
        <Grid container direction='row' wrap='nowrap'>
          {props.abTest &&
            props.abTest.Tests &&
            props.abTest.Tests.map((test) => {
              const { Name, Parameters, Share } = test;
              const params = parseJSONParamsSafely(Parameters);
              if (Object.keys(params).length === 0) {
                return (
                  <Grid
                    item
                    style={{
                      width: '50%',
                      margin: '10px 3px',
                      border: '1px solid lightgray',
                      padding: '5px 20px',
                    }}
                  >
                    <h3>Current test params are not parsing correctly.</h3>
                  </Grid>
                );
              } else {
                return (
                  <Grid
                    item
                    style={{
                      width: '50%',
                      margin: '10px 3px',
                      border: '1px solid lightgray',
                      padding: '5px 20px',
                    }}
                  >
                    <h3 style={{ marginBottom: '5px' }}>
                      {Name === '' ? 'A' : Name} -{' '}
                      {Name === '' ? 'Current Parameters' : 'Test Parameters'}
                    </h3>
                    <p style={{ fontSize: '1rem', marginTop: '0px', marginBottom: '18px' }}>
                      Percentage of Traffic: {Share + ' %'}
                    </p>
                    {changedFields.includes('BidFloor') && (
                      <p>
                        Bid Floor:{' '}
                        {formatCurrency(
                          params.BidFloor,
                          '0',
                          publisher.ExchangeRate.ExchangeValueToUSD,
                          convertECurrencyTypeToSymbol(publisher.CurrencyDisplayed),
                        )}
                      </p>
                    )}
                    {/* {changedFields.includes('FrequencyCap') && <p>Request Cap: {formatHugeNumber(params.FrequencyCap)}</p>} */}
                    {changedFields.includes('MinAdDurationOverride') && (
                      <p>Min Ad Duration: {params.MinAdDurationOverride}</p>
                    )}
                    {changedFields.includes('MaxAdDurationOverride') && (
                      <p>Max Ad Duration: {params.MaxAdDurationOverride}</p>
                    )}
                    {changedFields.includes('AppBundleOverride') && (
                      <p>App Bundle Override: {params.AppBundleOverride}</p>
                    )}
                    {changedFields.includes('AppNameOverride') && (
                      <p>App Name Override: {params.AppNameOverride}</p>
                    )}
                    {changedFields.includes('AppDomainOverride') && (
                      <p>App Name Override: {params.AppDomainOverride}</p>
                    )}
                    {changedFields.includes('PoddedRequestActive') && (
                      <p>
                        Podded Request:{' '}
                        {params.PoddedRequestActive === HeaderBidderPoddedRequestActiveActive
                          ? 'Active'
                          : 'Inactive'}
                      </p>
                    )}
                    {changedFields.includes('MultiBidActive') && (
                      <p>
                        Multi Bid:{' '}
                        {params.MultiBidActive === EBool.EBool_TRUE ? 'Active' : 'Inactive'}
                      </p>
                    )}
                    {changedFields.includes('NumberOfAds') &&
                      BIDDERS_MULTI_BID_ENABLED.includes(props.bidderParams.Bidder) && (
                        <p>
                          Number of Ads:{' '}
                          {params.NumberOfAds === 0 ? 'None' : '' + params.NumberOfAds}
                        </p>
                      )}
                    {changedFields.includes('Pods') && <p>Targeted Slots: {params.Pods}</p>}
                    {changedFields.includes('UseSyntheticIDActive') && (
                      <p>
                        Use Synthetic ID:{' '}
                        {params.UseSyntheticIDActive === EBool.EBool_TRUE ? 'Active' : 'Inactive'}
                      </p>
                    )}
                    {changedFields.includes('UseTruncatedIPActive') && (
                      <p>
                        Use Truncated IP:{' '}
                        {params.UseTruncatedIPActive === EBool.EBool_TRUE ? 'Active' : 'Inactive'}
                      </p>
                    )}
                    {changedFields.length === 0 && (
                      <strong>Current test params are not parsing correctly.</strong>
                    )}
                  </Grid>
                );
              }
            })}
        </Grid>
      </Grid>
    );
  };

  const renderNewTest = () => {
    const { newTest, bidderParams, errors, publisher } = props;
    if (!newTest || !bidderParams || !errors) {
      return;
    }
    const currencySymbol = convertECurrencyTypeToSymbol(publisher.CurrencyDisplayed);
    return (
      <Rows>
        {props.renderFlightDates(props.newTest)}
        <Row noPadding>
          <Cols rowBreakSize='medium'>
            {/* A - CURRENT PARAMETERS */}
            <Col
              flex
              style={{
                margin: '10px 3px',
                border: '1px solid lightgray',
                padding: '5px 20px',
              }}
            >
              <h3>A - Current Parameters</h3>
              <p style={{ fontSize: '1rem', marginTop: '0px', marginBottom: '18px' }}>
                Percentage of Traffic: {props.ATestShare + ' %'}
              </p>
              {(() => {
                const params = bidderParams as { Bidder?: DemandSource };
                const bidder = (params?.Bidder || '') as DemandSource;
                return ![DemandSources.publicaCampaign, DemandSources.publica].includes(bidder);
              })() && (
                <p>
                  Bid Floor:{' '}
                  {formatCurrency(
                    bidderParams.BidFloor,
                    '0',
                    publisher.ExchangeRate.ExchangeValueToUSD,
                    convertECurrencyTypeToSymbol(publisher.CurrencyDisplayed),
                  )}
                </p>
              )}
              {/* <p>Request Cap: {formatHugeNumber(bidderParams.FrequencyCap)}</p> */}
              <p>Min Ad Duration: {bidderParams.MinAdDurationOverride}</p>
              <p>Max Ad Duration: {bidderParams.MaxAdDurationOverride}</p>
              <p>App Bundle Override: {bidderParams.AppBundleOverride}</p>
              <p>App Name Override: {bidderParams.AppNameOverride}</p>
              <p>App Domain Override: {bidderParams.AppDomainOverride}</p>
              {BIDDERS_PODDED_REQUEST_ENABLED.includes(bidderParams.Bidder) && (
                <p>
                  Podded Request:{' '}
                  {bidderParams.PoddedRequestActive === HeaderBidderPoddedRequestActiveActive
                    ? 'Active'
                    : 'Inactive'}
                </p>
              )}
              {BIDDERS_MULTI_BID_ENABLED.includes(bidderParams.Bidder) && (
                <p>
                  Multi Bid:{' '}
                  {bidderParams.MultiBidActive === EBool.EBool_TRUE ? 'Active' : 'Inactive'}
                </p>
              )}
              {
                // spotx has historical baggage, need to handle separately
                BIDDERS_MULTI_BID_ENABLED.includes(bidderParams.Bidder) &&
                  bidderParams.Bidder !== 'spotx' &&
                  bidderParams.MultiBidActive === EBool.EBool_TRUE && (
                    <p>Number Of Ads: {getNumberOfAds(bidderParams.Params)}</p>
                  )
              }
              {bidderParams.Bidder === 'spotx' &&
                bidderParams.MultiBidActive === EBool.EBool_TRUE && (
                  <p>Number Of Ads: {getSpotxNumberOfAds(bidderParams.Params)}</p>
                )}
              <p>Targeted Slots: {bidderParams.Slots.join(',')}</p>
              <p>
                Use Synthetic ID:{' '}
                {bidderParams.AnonymizeDeviceID === EBool.EBool_TRUE ? 'Active' : 'Inactive'}
              </p>
              <p>
                Use Truncated IP:{' '}
                {bidderParams.AnonymizeIP === EBool.EBool_TRUE ? 'Active' : 'Inactive'}
              </p>
            </Col>
            {/* B - TEST PARAMETERS */}
            <Col
              flex
              style={{
                margin: '10px 3px',
                border: '1px solid lightgray',
                padding: '5px 20px',
                overflow: 'hidden',
              }}
            >
              <Grid container direction='column'>
                <h3>B - Test Parameters</h3>
                {/* Percentage of Traffic */}
                <Grid item>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: '15px',
                      width: '400px',
                    }}
                  >
                    <Typography variant='body1' style={{ paddingRight: '8px' }}>
                      Percentage of Traffic:
                    </Typography>
                    <TextField
                      name='Share'
                      value={newTest.Share + '%'}
                      disabled={false}
                      onChange={props.handleChange}
                      helperText={errors.Share}
                      error={!!errors.Share}
                      style={{ width: 64 }}
                    />
                  </div>
                </Grid>
                {/* Bid Floor */}
                {(() => {
                  const params = props.bidderParams as { Bidder?: DemandSource };
                  const bidder = (params?.Bidder || '') as DemandSource;
                  return ![DemandSources.publicaCampaign, DemandSources.publica].includes(bidder);
                })() && (
                  <Grid item>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: '10px',
                        width: '400px',
                      }}
                    >
                      <Typography variant='subtitle2' style={{ paddingRight: '8px' }}>
                        Bid Floor:
                      </Typography>
                      <TextField
                        name='BidFloor'
                        value={newTest.BidFloor}
                        disabled={false}
                        onChange={props.handleChange}
                        helperText={errors.BidFloor}
                        error={!!errors.BidFloor}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              {currencySymbol === '$' || newTest.BidFloor === ''
                                ? ''
                                : currencySymbol +
                                  (
                                    parseFloat(newTest.BidFloor) *
                                    publisher.ExchangeRate.ExchangeValueToUSD
                                  ).toFixed(2)}
                            </InputAdornment>
                          ),
                        }}
                        style={{ width: 64 }}
                      />
                    </div>
                  </Grid>
                )}
                {/* Min Ad Duration */}
                <Grid item>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: '10px',
                      width: '400px',
                    }}
                  >
                    <Typography variant='subtitle2' style={{ paddingRight: '8px' }}>
                      Min Ad Duration:
                    </Typography>
                    <TextField
                      name='MinAdDurationOverride'
                      value={newTest.MinAdDurationOverride}
                      disabled={false}
                      onChange={props.handleChange}
                      helperText={errors.MinAdDurationOverride}
                      error={!!errors.MinAdDurationOverride}
                      style={{ width: 64 }}
                    />
                  </div>
                </Grid>
                {/* Max Ad Duration */}
                <Grid item>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: '10px',
                      width: '400px',
                    }}
                  >
                    <Typography variant='subtitle2' style={{ paddingRight: '8px' }}>
                      Max Ad Duration:
                    </Typography>
                    <TextField
                      name='MaxAdDurationOverride'
                      value={newTest.MaxAdDurationOverride}
                      disabled={false}
                      onChange={props.handleChange}
                      helperText={errors.MaxAdDurationOverride}
                      error={!!errors.MaxAdDurationOverride}
                      style={{ width: 64 }}
                    />
                  </div>
                </Grid>
                {/* App Bundle Override */}
                <Grid item>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: '10px',
                      width: '400px',
                    }}
                  >
                    <Typography variant='subtitle2' style={{ paddingRight: '8px' }}>
                      App Bundle Override:
                    </Typography>
                    <TextField
                      name='AppBundleOverride'
                      value={newTest.AppBundleOverride}
                      disabled={false}
                      onChange={props.handleChange}
                      style={{ width: 192 }}
                    />
                  </div>
                </Grid>
                {/* App Name Override */}
                <Grid item>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: '10px',
                      width: '400px',
                    }}
                  >
                    <Typography variant='subtitle2' style={{ paddingRight: '8px' }}>
                      App Name Override:
                    </Typography>
                    <TextField
                      name='AppNameOverride'
                      value={newTest.AppNameOverride}
                      disabled={false}
                      onChange={props.handleChange}
                      style={{ width: 192 }}
                    />
                  </div>
                </Grid>
                <Grid item>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: '10px',
                      width: '400px',
                    }}
                  >
                    <Typography variant='subtitle2' style={{ paddingRight: '8px' }}>
                      App Domain Override:
                    </Typography>
                    <TextField
                      name='AppDomainOverride'
                      value={newTest.AppDomainOverride}
                      disabled={false}
                      onChange={props.handleChange}
                      style={{ width: 192 }}
                    />
                  </div>
                </Grid>
                {/* Podded Request */}
                {
                  // spotx do no support podded request
                  BIDDERS_PODDED_REQUEST_ENABLED.includes(bidderParams.Bidder) && (
                    <Grid item>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          marginBottom: '10px',
                          width: '400px',
                        }}
                      >
                        <Typography variant='subtitle2' style={{ paddingRight: '8px' }}>
                          Podded Request:
                        </Typography>
                        <Switch
                          checked={
                            Number(newTest.PoddedRequestActive) ===
                            HeaderBidderPoddedRequestActiveActive
                          }
                          disabled={Number(newTest.MultiBidActive) === EBool.EBool_TRUE}
                          onChange={props.handleChange}
                          name='PoddedRequestActive'
                        />
                      </div>
                    </Grid>
                  )
                }
                {/* Multi Bid */}
                {BIDDERS_MULTI_BID_ENABLED.includes(bidderParams.Bidder) && (
                  <Grid item>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: '10px',
                        width: '400px',
                      }}
                    >
                      <Typography variant='subtitle2' style={{ paddingRight: '8px' }}>
                        Multi Bid:
                      </Typography>
                      <Switch
                        checked={Number(newTest.MultiBidActive) === EBool.EBool_TRUE}
                        disabled={
                          Number(newTest.PoddedRequestActive) ===
                          HeaderBidderPoddedRequestActiveActive
                        }
                        onChange={props.handleChange}
                        name='MultiBidActive'
                      />
                    </div>
                  </Grid>
                )}
                {/* Number Of Ads */}
                {BIDDERS_MULTI_BID_ENABLED.includes(bidderParams.Bidder) &&
                  Number(newTest.MultiBidActive) === EBool.EBool_TRUE && (
                    <Grid item>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          marginBottom: '10px',
                          width: '400px',
                        }}
                      >
                        <Typography variant='subtitle2' style={{ paddingRight: '8px' }}>
                          Number Of Ads:
                        </Typography>
                        <Select
                          name='NumberOfAds'
                          value={newTest.NumberOfAds}
                          onChange={props.handleChange}
                          disabled={
                            Number(newTest.PoddedRequestActive) ===
                            HeaderBidderPoddedRequestActiveActive
                          }
                          input={<Input />}
                          renderValue={(selected: any) => (selected === '0' ? 'None' : selected)}
                        >
                          {generateNumberOfAdsOptions().map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              <Checkbox checked={newTest.NumberOfAds === option.key} />
                              <ListItemText primary={option.text} />
                            </MenuItem>
                          ))}
                        </Select>
                      </div>
                    </Grid>
                  )}
                {/* Targeted Slots (=Pods) */}
                <Grid item>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: '10px',
                      width: '400px',
                    }}
                  >
                    <Typography variant='subtitle2' style={{ paddingRight: '8px' }}>
                      Targeted Slots:
                    </Typography>
                    <Select
                      multiple
                      name='Pods'
                      value={newTest.Pods.length > 0 ? newTest.Pods.split(',') : ['all']}
                      onChange={props.handleChange}
                      input={<Input />}
                      renderValue={(selected: any) => selected.join(',')}
                    >
                      {generateSlotOptions().map((podOption) => (
                        <MenuItem key={podOption.key} value={podOption.key}>
                          <Checkbox
                            checked={
                              newTest.Pods.split(',').includes(podOption.key) ||
                              (podOption.key === 'all' && newTest.Pods === '')
                            }
                          />
                          <ListItemText primary={podOption.text} />
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </Grid>
                {/* Use Synthetic ID */}
                <Grid item>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: '10px',
                      width: '400px',
                    }}
                  >
                    <Typography variant='subtitle2' style={{ paddingRight: '8px' }}>
                      Use Synthetic ID:
                    </Typography>
                    <Switch
                      checked={Number(newTest.UseSyntheticIDActive) === EBool.EBool_TRUE}
                      disabled={Number(newTest.MultiBidActive) === EBool.EBool_TRUE}
                      onChange={props.handleChange}
                      name='UseSyntheticIDActive'
                    />
                  </div>
                </Grid>
                {/* Use Truncated IP */}
                <Grid item>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: '10px',
                      width: '400px',
                    }}
                  >
                    <Typography variant='subtitle2' style={{ paddingRight: '8px' }}>
                      Use Truncated IP:
                    </Typography>
                    <Switch
                      checked={Number(newTest.UseTruncatedIPActive) === EBool.EBool_TRUE}
                      disabled={Number(newTest.MultiBidActive) === EBool.EBool_TRUE}
                      onChange={props.handleChange}
                      name='UseTruncatedIPActive'
                    />
                  </div>
                </Grid>
              </Grid>
            </Col>
          </Cols>
        </Row>
      </Rows>
    );
  };

  return (
    <div
      style={{
        background: '#FFFFFF 0% 0% no-repeat padding-box',
        boxShadow: '0px 3px 6px #CBCBCB',
        borderRadius: smallRadius,
        opacity: '1',
        paddingTop: '1px',
        marginBottom: '20px',
      }}
    >
      <Grid
        container
        direction='row'
        justify='space-between'
        alignItems='center'
        style={{ height: '55px', padding: '10px 20px' }}
      >
        <Grid item xs={6}>
          {testType === ABTestMode.New && props.handleChange && (
            <ModernTextField value={props.newTest.Name} onChange={props.handleChange} />
          )}
          {testType !== ABTestMode.New && abTest && (
            <Typography style={{ width: '100%' }}>{abTest.Name}</Typography>
          )}
        </Grid>
        <Button onClick={() => props.handleCollapse(index)}>
          {abTestCollapsibleStatus[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
      </Grid>
      <Grid container direction='column' justify='flex-start' alignItems='flex-start'>
        <Collapse style={{ width: '100%' }} in={abTestCollapsibleStatus[index]}>
          <Divider variant={'fullWidth'} style={{ height: '2px' }} />
          <div
            style={{
              padding: '1%',
            }}
          >
            {testType !== ABTestMode.New && props.abTest && renderExistingTest()}
            {testType === ABTestMode.New && props.newTest && props.errors && renderNewTest()}

            {/* Buttons */}
            <Grid container direction='row' justify='flex-start'>
              {/* Launch test dialog and button */}
              {testType === ABTestMode.New && (
                <ConfirmationDialogButton
                  title='Confirm Launch Test'
                  description={confirmLaunchTestDescription}
                  notice={confirmLaunchTestNotice}
                  acceptButtonTitle='Confirm'
                  acceptButtonCallBack={handleLaunchTest}
                  refuseButtonTitle='Cancel'
                >
                  <Button
                    disabled={props.disabled}
                    variant='contained'
                    color='primary'
                    style={{ margin: '0px 10px', backgroundColor: getColorTheme('primaryButton') }}
                  >
                    Launch Test
                  </Button>
                </ConfirmationDialogButton>
              )}

              {/* Cancel button */}
              {testType === ABTestMode.New && <Button onClick={handleCancelTest}>Cancel</Button>}

              {/* Implement test parameters dialog and button */}
              {testType === ABTestMode.Past && (
                <ConfirmationDialogButton
                  title='Confirm Implement Test Params'
                  description={confirmImplementTestParamsDescription}
                  acceptButtonTitle='Confirm'
                  acceptButtonCallBack={() => handleImplementParameters(index)}
                  refuseButtonTitle='Cancel'
                >
                  <ModelSecondaryButton disabled={props.disabled} style={{ margin: '0px 10px' }}>
                    Implement Test Parameters
                  </ModelSecondaryButton>
                </ConfirmationDialogButton>
              )}

              {/* End test button */}
              {testType === ABTestMode.Current && (
                <ConfirmationDialogButton
                  title='Confirm End Test'
                  description={confirmEndTestDescription}
                  acceptButtonTitle='Confirm'
                  acceptButtonCallBack={() => handleEndTest(index)}
                  refuseButtonTitle='Cancel'
                >
                  <ModelSecondaryButton disabled={props.disabled} style={{ margin: '0px 10px' }}>
                    End Test
                  </ModelSecondaryButton>
                </ConfirmationDialogButton>
              )}
            </Grid>
          </div>
        </Collapse>
      </Grid>
    </div>
  );
};
